import { StrictMode, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { icons } from './components/Icons';
import { Rail } from './components/Rail';
import { Account } from './pages/Account';
import { Activate } from './pages/Activate';
import { Journeys } from './pages/Journeys';
import { LiveMap } from './pages/LiveMap';
import { Login } from './pages/Login';
import { GuidePage, TermsPage } from './pages/Public';
import { GeofenceEdit } from './pages/GeofenceEdit';
import { Geofences } from './pages/Geofences';
import './styles.css';

// The customer app. Staff work in Sprout (crm/) and come here through "open the app as",
// which lands on /view-as#<one-time token>.
function ViewAs() {
  const { viewAs } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = window.location.hash.slice(1);
    history.replaceState(null, '', '/view-as');
    viewAs(token).then(() => navigate('/', { replace: true })).catch((e) => setError(e.message));
  }, [viewAs, navigate]);
  return <div className="center">{error ? <p className="error" role="alert">{error}</p> : <p className="muted">Opening the app…</p>}</div>;
}

function Shell() {
  const { user, checking, logout } = useAuth();
  const { pathname } = useLocation();
  if (pathname === '/view-as') return <ViewAs />;
  // Public pages: activating a self-install tracker, its install guide, the terms.
  if (/^\/(activate|guide|terms)(\/|$)/.test(pathname)) {
    return (
      <Routes>
        <Route path="/activate" element={<Activate />} />
        <Route path="/activate/:token" element={<Activate />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/guide/:model" element={<GuidePage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    );
  }
  if (checking) return <div className="center muted">Loading…</div>;
  if (!user) return <Login />;
  if (user.mustChangePassword) return <Account forced />;
  return (
    <div className="app">
      {user.impersonatedBy && (
        <div className="viewas" role="status">
          Viewing as <strong>{user.name}</strong> ({user.customerName}), opened by {user.impersonatedBy.name}.
          <button className="link" onClick={() => void logout()}>End</button>
        </div>
      )}
      <Rail product="Vehicle tracking" user={user} onLogout={() => void logout()} items={[
        { to: '/', end: true, label: 'Live map', icon: icons.map, phone: true, match: (p) => p === '/' || /^\/vehicles\/\d+\/journeys/.test(p) || p === '/journeys' },
      ]} />
      <main>
        <Routes>
          <Route path="/" element={<LiveMap />} />
          <Route path="/journeys" element={<Journeys />} />
          <Route path="/vehicles/:id/journeys" element={<Journeys />} />
          <Route path="/vehicles/:id/journeys/:start" element={<Journeys />} />
          <Route path="/geofences" element={<Geofences />} />
          <Route path="/geofences/:id" element={<GeofenceEdit />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
